import React, { useState, useMemo, useEffect } from 'react';
import { 
  MapPin, Calendar, Users, ArrowLeft, Heart, ChevronRight,
  TrendingUp, Gift, Check, Loader2
} from 'lucide-react';
import GoldenParticles from './GoldenParticles';
import '../RegioesSociais.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const RegioesSociais = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [socialActions, setSocialActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Buscar ações da API
  useEffect(() => {
    const fetchActions = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/actions?status=publicado&limit=100`);
        if (response.ok) {
          const data = await response.json();
          if (data.actions) {
            setSocialActions(data.actions);
          }
        } else {
          setError('Erro ao carregar ações');
        }
      } catch (err) {
        console.error('Erro ao buscar ações:', err);
        setError('Erro ao conectar com o servidor');
      } finally {
        setLoading(false);
      }
    };
    fetchActions();
  }, []);

  // Agrupar ações por região
  const regionGroups = useMemo(() => {
    const groups = {};
    
    socialActions.forEach(action => {
      const location = action.location;
      if (!groups[location]) {
        groups[location] = {
          location,
          actions: [],
          totalBeneficiaries: 0,
          latestDate: action.date
        };
      }
      groups[location].actions.push(action);
      
      // Extrair número de beneficiários
      const match = action.beneficiaries?.match(/\d+/);
      if (match) {
        groups[location].totalBeneficiaries += parseInt(match[0]);
      }
    });

    return Object.values(groups);
  }, [socialActions]);

  // Calcular estatísticas totais
  const totalStats = useMemo(() => {
    let actions = 0;
    let beneficiaries = 0;

    regionGroups.forEach(region => {
      actions += region.actions.length;
      beneficiaries += region.totalBeneficiaries;
    });

    return { actions, beneficiaries, regions: regionGroups.length };
  }, [regionGroups]);

  return (
    <div className="regioes-page">
      <GoldenParticles />
      
      {/* HEADER */}
      <section className="regioes-header">
        <div className="regioes-header-bg"></div>
        <div className="regioes-header-content">
          <a href="/acoes-sociais" className="regioes-back">
            <ArrowLeft size={20} />
            <span>Voltar para Ações</span>
          </a>

          <div className="regioes-title">
            <span className="regioes-badge">IMPACTO REAL</span>
            <h1>
              AÇÃO <span className="gradient-gold">BENEFICENTE</span>
            </h1>
            <p>Todos juntos em uma só missão</p>
          </div>

          {/* ESTATÍSTICAS GLOBAIS */}
          <div className="regioes-global-stats">
            <div className="regioes-global-stat">
              <TrendingUp size={28} />
              <div>
                <span className="value">{totalStats.regions}</span>
                <span className="label">Regiões Atendidas</span>
              </div>
            </div>
            <div className="regioes-global-stat">
              <Gift size={28} />
              <div>
                <span className="value">{totalStats.actions}</span>
                <span className="label">Ações Concluídas</span>
              </div>
            </div>
            <div className="regioes-global-stat">
              <Users size={28} />
              <div>
                <span className="value">{totalStats.beneficiaries.toLocaleString()}+</span>
                <span className="label">Vidas Impactadas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GRID DE REGIÕES */}
      <section className="regioes-grid-section">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
            <Loader2 size={48} style={{ color: '#d4af37', animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#888' }}>Carregando regiões...</p>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
            <p style={{ color: '#ff6b6b' }}>{error}</p>
          </div>
        ) : regionGroups.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem', flexDirection: 'column', gap: '1rem' }}>
            <Gift size={48} style={{ color: '#888' }} />
            <p style={{ color: '#888' }}>Nenhuma região cadastrada ainda</p>
          </div>
        ) : (
        <div className="regioes-grid">
          {regionGroups.map((region, index) => (
            <div 
              key={region.location}
              className={`regiao-card ${index === 0 ? 'destaque' : ''}`}
              onClick={() => setSelectedRegion(region)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* IMAGEM DE FUNDO */}
              <div className="regiao-card-bg">
                <img 
                  src={region.actions[0]?.media?.[0]?.url || region.actions[0]?.media?.[0]?.thumbnail}
                  alt={region.location}
                />
                <div className="regiao-card-overlay"></div>
              </div>

              {/* BADGE DE AÇÕES */}
              <div className="regiao-badge">
                <Check size={16} />
                {region.actions.length} {region.actions.length === 1 ? 'ação' : 'ações'}
              </div>

              {/* CONTEÚDO */}
              <div className="regiao-card-content">
                <div className="regiao-location">
                  <MapPin size={18} />
                  <span>{region.location}</span>
                </div>
                
                <h3>{region.location.split(' - ')[0]}</h3>

                <div className="regiao-stats">
                  <div className="regiao-stat">
                    <span className="value">{region.totalBeneficiaries.toLocaleString()}+</span>
                    <span className="label">beneficiários</span>
                  </div>
                </div>

                <button className="regiao-btn">
                  <span>Ver ações</span>
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
        )}
      </section>

      {/* MODAL DE REGIÃO */}
      {selectedRegion && (
        <div className="regiao-modal-overlay" onClick={() => setSelectedRegion(null)}>
          <div className="regiao-modal" onClick={(e) => e.stopPropagation()}>
            <button className="regiao-modal-close" onClick={() => setSelectedRegion(null)}>
              ✕
            </button>

            <div className="regiao-modal-header">
              <span className="regiao-modal-badge">REGIONAL</span>
              <h2>{selectedRegion.location}</h2>
              
              <div className="regiao-modal-stats">
                <div className="regiao-modal-stat">
                  <span className="value">{selectedRegion.actions.length}</span>
                  <span className="label">ações<br/>realizadas</span>
                </div>
                <div className="regiao-modal-stat highlight">
                  <span className="value">{selectedRegion.totalBeneficiaries.toLocaleString()}+</span>
                  <span className="label">beneficiários<br/>impacto total</span>
                </div>
              </div>
            </div>

            <div className="regiao-modal-body">
              <h3>AÇÕES REALIZADAS</h3>
              
              <div className="regiao-acoes-lista">
                {selectedRegion.actions.map(action => (
                  <div key={action.id} className="regiao-acao-item">
                    <div className="regiao-acao-status">
                      <span className="status-badge concluido">
                        <Check size={14} />
                        Concluído
                      </span>
                    </div>

                    <div className="regiao-acao-content">
                      <h4>{action.title.replace(/🎁|🤝|💙|🎉|⚽|🐶|💛|🎅/g, '').trim()}</h4>
                      <p>{action.description.substring(0, 200)}...</p>

                      <div className="regiao-acao-meta">
                        <span>
                          <Calendar size={14} />
                          {action.date}
                        </span>
                        <span>
                          <Users size={14} />
                          {action.beneficiaries}
                        </span>
                      </div>

                      <a href={`/acoes-sociais`} className="regiao-acao-link">
                        <Heart size={16} />
                        Ver galeria de fotos
                      </a>
                    </div>

                    {action.media?.[0] && (
                      <div className="regiao-acao-thumb">
                        <img src={action.media[0].thumbnail || action.media[0].url} alt="" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegioesSociais;
